import React, {
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useState,
} from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Grid,
  InputAdornment,
  Pagination,
  Stack,
  Backdrop,
  useTheme,
  Typography,
  MenuItem,
  Popover,
} from "@mui/material";
import dayjs from "dayjs";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import useMediaQuery from "@mui/material/useMediaQuery";
import { PAGE_SIZE } from "../../Services/common/const";
import Title from "../../Components/Title";
import ShortcutButton from "../../Components/ShortcutButton";
import { PreferencesContext } from "../../PreferencesContext";
import MissingConsumer from "../../Components/MissingConsumer";
import { ExportFilesFunction, numberFormat, useQuery, } from "../../shared/CommonFuctions";
import { KeyboardArrowDownSharp } from "@mui/icons-material";
import KeyboardBackspaceRoundedIcon from "@mui/icons-material/KeyboardBackspaceRounded";
import SplashComponent from "../../Navigation/splash";
import { AlertType } from "../../shared/Modal/AlertType";
import { EncryptDecryptService } from "../../Services/common/EncryptionService";
import { StyledMenu } from "../../Components/StyleMenus";
import { format } from "date-fns";
import CustomDateRangePicker from "../../Components/CustomDateRange";
import { GetPurchaseSummaryByProduct } from "../../Services/PurchaseService";
import { GetExpenseCategoryWiseReport, GetPurchaseSummaryByProductReport } from "../../Services/ReportService";
import { GetExpenseSummaryByCategory } from "../../Services/ExpenseService";

const parameters = {
  consumerId: 25,
  branchId: 1,
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  lessThanValue: "",
};

interface Data {
  labelId: number;
  expenseCategoryId: number;
  mappingCount: number;
  expenseCategoryName: string;
  voucherCount: number;
  grandTotal: number;

}

const ExpenseSummaryByCategory = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [isLoading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [param, setParam] = useState(parameters);
  const [fromDate, setFromDate] = useState<any>(
    userContext.financialYearStartDate
  );
  const [anchorEl0, setAnchorEl0] = React.useState<HTMLButtonElement | null>(
    null
  );

  const [selectedVoucherTypes, setSelectedVoucherTypes] = useState<string[]>(
    []
  );
  const [toDate, setToDate] = useState<any>(userContext.financialYearEndDate);
  const [resultData, setResultData] = useState<Data[]>([]);
  const startDate = new Date(userContext.financialYearStartDate);
  const endDate = new Date(userContext.financialYearEndDate);
  const [selectedDateRange, setDateRange] = useState<any>([
    {
      startDate: startDate,
      endDate: endDate,
      key: "selection",
    },
  ]);
  const [anchorEl2, setAnchorEl2] = useState<null | HTMLElement>(null);
  const mobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const matches = useMediaQuery("(max-width:425px)");
  const edService = new EncryptDecryptService();
  const openExportType = Boolean(anchorEl2);

  const location = useLocation();
  let urlParams = useQuery();
  const encryptedLabelId = urlParams.get("q");
  let decryptLabelId: any = edService.decrypt(encryptedLabelId);
  let convertedLabelId: any = parseInt(decryptLabelId) || 0;

  const pageInfo = useMemo(
    () => ({
      startIndex: (page - 1) * PAGE_SIZE + 1,
      endIndex: (page - 1) * PAGE_SIZE + resultData.length,
      totalPages: Math.ceil(totalCount / PAGE_SIZE),
    }),
    [page, resultData.length, totalCount]
  );

  const handleCloseDate = () => {
    setAnchorEl0(null);
  };

  const handleSearch = useCallback((val: string) => {
    setSearchValue(val);
    setPage(1);
    setParam((prev) => ({ ...prev, searchValue: val, pageIndex: 1 }));
  }, []);

  const handlePageChange = useCallback(
    (event: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
      setParam((prev) => ({ ...prev, pageIndex: value }));
    },
    []
  );

  const handleMoveToVoucher = useCallback(
    async (expenseCategoryId: number) => {
      try {
        const encExpenseCategoryId = edService.encrypt(expenseCategoryId.toString());
        navigate({
          pathname: "/expense-voucher",
          search: `?q=${encodeURIComponent(encExpenseCategoryId || "")}`,
        });
      } catch (e: any) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: typeof e === "string" ? e : e?.message,
          alertType: AlertType.Error,
        });
      }
    },
    [navigate, edService, userContext, updateUserContext]
  );

  async function fetchExpenseSummaryByCategory() {
    try {
      setLoading(true);
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId.toString());
      params.append("firmId", userContext.firmId.toString());
      params.append("branchId", userContext.branchId.toString());
      params.append("pageIndex", page.toString());
      params.append("pageSize", PAGE_SIZE.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("startDate", format(new Date(fromDate), "yyyy-MM-dd"));
      params.append("endDate", format(new Date(toDate), "yyyy-MM-dd"));
      params.append("mode", "LIST");
      const result: any = await GetExpenseSummaryByCategory(params);
      if (result && result.data.data) {
        let expenseInfo =
          result.data.data?.expenseSummaryByCategoryCollection;
        let dataCount = result.data.data.totalCount;
        setResultData(expenseInfo);
        setTotalCount(dataCount);
        setLoading(false);
      } else {
        console.log("Error");
        setLoading(false);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  async function handelExpenseCategoryReportPopup(type: any) {
    setAnchorEl2(null);
    setLoading(true);
    let params = new URLSearchParams();
    var attachmentName = "";
    if (type === "pdf") {
      attachmentName = "ExpenseCategory.pdf";
    } else {
      attachmentName = "ExpenseCategory.xls";
    }
    params.append("reportName", "ExpenseCategory");
    params.append("reportType", type);
    params.append("consumerId", userContext.consumerId.toString());
    params.append("firmId", userContext.firmId.toString());
    params.append("branchId", userContext.branchId.toString());
    params.append("pageIndex", page.toString());
    params.append("pageSize", PAGE_SIZE.toString());
    params.append("sortColumn", param.sortColumn);
    params.append("sortDirection", param.sortDirection);
    params.append("searchValue", searchValue);
    params.append("startDate", format(new Date(fromDate), "yyyy-MM-dd"));
    params.append("endDate", format(new Date(toDate), "yyyy-MM-dd"));
    params.append("mode", "EXPORT");
    try {
      const result: any = await GetExpenseCategoryWiseReport(params);

      if (result && result.data && result.data.data) {
        var fileContent = result?.data?.data?.fileData;
        var attachmentData = fileContent;
        ExportFilesFunction(attachmentName, type, attachmentData);
        setLoading(false);
      }
      setLoading(false);
    } catch (e: any) {
      // setLoading(false);
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchExpenseSummaryByCategory();
  }, [page, param, fromDate, toDate,]);

  const handleCloseExportType = () => {
    setAnchorEl2(null);
  };

  const handleClickExportType = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl2(event.currentTarget);
  };


  const TableHeader = React.memo(() => (
    <TableHead>
      <TableRow>
        <TableCell
          sx={{
            display: {
              xs: "table-cell",
              sm: "table-cell",
              md: "table-cell",
              lg: "table-cell",
            },
            fontWeight: "bold",
            minWidth: "80px",
          }}
          align="left">
          {"Category Number"}
        </TableCell>
        <TableCell
          sx={{
            display: {
              xs: "table-cell",
              sm: "table-cell",
              md: "table-cell",
              lg: "table-cell",
            },
            fontWeight: "bold",
            minWidth: "80px",
          }}
          align="center">
          {"Voucher Count"}
        </TableCell>
        <TableCell
          sx={{
            display: {
              xs: "table-cell",
              sm: "table-cell",
              md: "table-cell",
              lg: "table-cell",
            },
            fontWeight: "bold",
            minWidth: "80px",
          }}
          align="right">
          {"Grand Total"}
        </TableCell>
      </TableRow>
    </TableHead>
  ));

  const TableRowComponent = React.memo(
    ({
      item,
      handleMoveToVoucher,
    }: {
      item: Data;
      handleMoveToVoucher: (id: number) => void;
    }) => (
      <TableRow hover role="checkbox" tabIndex={-1}>
        <TableCell
          sx={{
            display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
          }}
          align="left">
          {item?.expenseCategoryName}
        </TableCell>
        <TableCell
          sx={{
            display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
          }}
          align="center">
          <Button
            disableRipple
            color="inherit"
            onClick={() => handleMoveToVoucher(item.expenseCategoryId)}
            className="manuButton-link"
            style={{ backgroundColor: "transparent" }}>
            <span className="text-link" style={{ fontSize: "15px" }}>
              {item?.voucherCount}
            </span>
          </Button>
        </TableCell>
        <TableCell
          sx={{
            display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
          }}
          align="right">
          {numberFormat(item?.grandTotal)}
        </TableCell>
      </TableRow>
    )
  );


  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          backgroundColor: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}>
        <SplashComponent />
      </Backdrop>
      <Title pageTitle="Expense Summary By Category" />

      {userContext.consumerId > 0 ? (
        <Box
          className="boxContainer"
          sx={{
            "& > :not(style)": {
              width: "100%",
            },
          }}>
          <Grid
            className="title-button-responsive"
            item
            xs={6}
            md={3}
            sx={{
              display: "flex !important",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <h4 className="page-title">Expense Summary By Category</h4>

            <Grid
              item
              xs={6}
              md={3}
              marginRight={0}
              textAlign="end"
              paddingTop="0px">
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                size="medium"
              >
                <ShortcutButton label="ESC" />
                Back
              </Button>
            </Grid>
          </Grid>
          <Grid className="paper top-space">
            <Paper elevation={4} className="paper-container">
              <Grid
                container
                spacing={1}
                sx={{ marginTop: "10px" }}
                className="search-space">
                <Grid item xs={12} sm={12} md={3} lg={2} xl={2}>
                  <TextField
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSearch(searchValue);
                      }
                    }}
                    sx={{ paddingLeft: "0px !important", width: "100%" }}
                    className="button-responsive-stock"
                    placeholder="Search..."
                    type="text"
                    variant="outlined"
                    size="small"
                    onChange={(e) => setSearchValue(e.target.value)}
                    value={searchValue}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => handleSearch(searchValue)}>
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                      endAdornment: searchValue && (
                        <IconButton onClick={() => handleSearch("")}>
                          <ClearIcon />
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={2} xl={2}></Grid>
                <Grid item xs={12} sm={12} md={12} lg={8} xl={8}>
                  <Grid
                    sx={{
                      height: "100%",
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "center",
                      gap: "20px",
                      flexDirection: matches ? "column" : "row",
                    }}>
                    <CustomDateRangePicker
                      fromDate={fromDate}
                      setFromDate={setFromDate}
                      toDate={toDate}
                      setToDate={setToDate}
                    />
                    <Popover
                      open={Boolean(anchorEl0)}
                      anchorEl={anchorEl0}
                      onClose={handleCloseDate}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}>
                    </Popover>
                    <Button
                      aria-controls={
                        openExportType ? "demo-customized-menu" : undefined
                      }
                      aria-haspopup="true"
                      aria-expanded={openExportType ? "true" : undefined}
                      disabled={resultData.length <= 0}
                      variant="outlined"
                      disableElevation
                      onClick={handleClickExportType}
                      startIcon={<DownloadIcon />}
                      endIcon={<KeyboardArrowDownSharp />}>
                      Export
                    </Button>
                    <StyledMenu
                      id="demo-customized-menu"
                      MenuListProps={{
                        "aria-labelledby": "demo-customized-button",
                      }}
                      anchorEl={anchorEl2}
                      open={openExportType}
                      onClose={handleCloseExportType}>
                      <MenuItem onClick={() => handelExpenseCategoryReportPopup("pdf")}>
                        PDF
                      </MenuItem>
                      <MenuItem onClick={() => handelExpenseCategoryReportPopup("Excel")}>
                        EXCEL
                      </MenuItem>
                    </StyledMenu>
                  </Grid>
                </Grid>
              </Grid>

              <TableContainer>
                <Table aria-labelledby="tableTitle" size="medium">
                  <TableHeader />
                  {resultData?.length > 0 && (
                    <TableBody>
                      {resultData.map((item: Data, index: number) => (
                        <TableRowComponent
                          key={item.expenseCategoryId || index}
                          item={item}
                          handleMoveToVoucher={handleMoveToVoucher}
                        />
                      ))}

                      <TableRow>
                        <TableCell align="left" sx={{ fontWeight: "bold" }}>
                          Total
                        </TableCell>

                        <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {numberFormat(
                            resultData.reduce(
                              (sum, item) => sum + (Number(item.grandTotal) || 0),
                              0
                            )
                          )}
                        </TableCell>

                      </TableRow>
                    </TableBody>
                  )}

                  {(!resultData || resultData.length <= 0) && (
                    <TableBody>
                      <TableRow hover tabIndex={-1} key={"0"}>
                        <TableCell colSpan={7} align="center">
                          No record(s) found
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
                {resultData?.length > 0 && (
                  <Grid
                    container
                    paddingTop={1}
                    alignItems={"center"}
                    justifyContent={"center"}>
                    <Grid item xs={12} sm={6} className="pagination-label">
                      {pageInfo.startIndex} - {pageInfo.endIndex} of{" "}
                      {totalCount}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack
                        component="div"
                        alignItems={mobileScreen ? "center" : "end"}
                        justifyContent={"center"}>
                        <Pagination
                          color="primary"
                          count={pageInfo.totalPages}
                          page={page}
                          onChange={handlePageChange}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                )}
              </TableContainer>
            </Paper>
          </Grid>
        </Box>
      ) : (
        <Box
          className="boxContainer"
          sx={{
            "& > :not(style)": {
              width: "100%",
            },
          }}>
          <MissingConsumer consumerId={userContext.consumerId} />
        </Box>
      )}
    </>
  );
};

export default React.memo(ExpenseSummaryByCategory);
